<template>
  <div class="address-info">
    <div class="bg-white border border-border rounded-md lg:mb-5 mb-4">
      <div class="flex w-full border-b border-border !m-0 p-4 gap-[8px] items-center">
        <span class="number-badge"><span v-if="(state.opt_type != 'general' && state.opt_type != 'SEIU721')">1</span><span
            v-if="(state.opt_type == 'general' || state.opt_type == 'SEIU721')">2</span></span>
        <h6 class="card-title !m-0">Address Information</h6>
      </div>
      <div class="p-4">
        <div class="form-group mb-4">
          <label id="address-label" for="address" class="address-label font-normal text-mute text-sm mb-2 inline-block">
            Your Address<span class="required-asterisk text-error">*</span>
          </label>
          <vue-google-autocomplete class="mt-1" id="address"
            classname="w-full text-black border border-blue-100 rounded py-2 px-4 mb-3 disabled:opacity-60" placeholder="Enter Your Address"
            ref="address" :disabled="disabled" country="us" :fields="googleAutocompleteFields" @blur="v$.form.address.$touch()" :types="['address']" v-model="form.address"
            v-on:placechanged="handlePlaceChange">
          </vue-google-autocomplete>
          <small v-if="v$.form.address.$error" class="text-error text-xs">
            Address is required.
          </small>
          <small class="mt-[8px] block text-mute text-xs font-normal">
            As you type, you will see a possible list of addresses. Select the correct address.
          </small>
        </div>
        <div class="form-group mb-4">
          <label for="address1" class="address-label font-normal text-mute text-sm mb-2 inline-block">
            Address Line 1<span class="required-asterisk text-error">*</span>
          </label>
          <input id="address1" type="text" :disabled="disabled" placeholder="Enter Address Line 1" class="form disabled:opacity-60" v-model="form.address1" @blur="v$.form.address1.$touch()" />
          <small v-if="v$.form.address1.$error" class="text-error text-xs">
            Address Line 1 is required.
          </small>
        </div>
        <div class="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div>
          <label for="address2" class="address-label font-normal text-mute text-sm mb-2 inline-block">
            Address Line 2
          </label>
          <input id="address2" type="text" :disabled="disabled" placeholder="Enter Address Line 2" class="form disabled:opacity-60" v-model="form.address2" />
        </div>
        <div>
          <label for="county" class="address-label font-normal text-mute text-sm mb-2 inline-block">
            County
          </label>
          <input id="county" type="text" :disabled="disabled" placeholder="Enter County" class="form disabled:opacity-60" v-model="form.county" />
        </div>
        </div>
        <div class="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div>
            <label for="city" class="address-label font-normal text-mute text-sm mb-2 inline-block">
              City<span class="required-asterisk text-error">*</span>
            </label>
            <input id="city" type="text" :disabled="disabled" placeholder="Enter City" class="form disabled:opacity-60" v-model="form.city"
              @blur="v$.form.city.$touch()" />
            <small v-if="v$.form.city.$error" class="text-error text-xs">
              City is required.
            </small>
          </div>
          <div>
            <label for="state" class="address-label font-normal text-mute text-sm mb-2 inline-block">
              State<span class="required-asterisk text-error">*</span>
            </label>
            <input id="state" type="text" :disabled="disabled" placeholder="Enter State" class="form disabled:opacity-60" v-model="form.state" @blur="v$.form.state.$touch()" />
            <small v-if="v$.form.state.$error" class="text-error text-xs">
              State is required.
            </small>
          </div>
          <div>
            <label for="zip" class="address-label font-normal text-mute text-sm mb-2 inline-block">
              ZIP Code<span class="required-asterisk text-error">*</span>
            </label>
            <input id="zip" type="text" :disabled="disabled" placeholder="Enter ZIP Code" class="form disabled:opacity-60" v-model="form.zip" @input="form.zip = form.zip.trim()" @blur="v$.form.zip.$touch()" />
            <small v-if="v$.form.zip.$error" class="text-error text-xs">
              ZIP Code is required.
            </small>
          </div>
        </div>
      </div>
    </div>
    <div v-if="state.enroll_msg" class="bg-red-500 border border-border rounded-md lg:mb-5 mb-4 hidden lg:block mt-5">
      <div class="p-4 flex items-start justify-start gap-4">
        <span
          class="w-[50px] h-[50px] bg-primaryLight border border-border flex rounded-md items-center justify-center shrink-0">
          <img src="@/assets/images/info.svg" alt="info">
        </span>
        <div>
          <p class="content !text-white">
            Thank you for your interest. At this time, U.S. Legal is not accepting enrollments in your state. If you
            have
            questions, please contact Customer Service at
           (800) 356-LAWS.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import { onMounted, reactive, watchEffect } from "vue";
import { computed } from "vue";
import Swal from "sweetalert2";
import VueGoogleAutocomplete from "vue-google-autocomplete"; // For address autocomplete from Google
import filter from "lodash/filter";

const GOOGLE_AUTOCOMPLETE_FIELDS = [
  "address_components",
  "formatted_address",
  "geometry",
  "name",
  "place_id",
  "type",
];

const getPostalCode = (rawPlace, fallbackText = "") => {
  for (const component of rawPlace?.address_components || []) {
    if (component.types?.includes("postal_code")) {
      return component.short_name;
    }
  }
  const match = String(
    rawPlace?.formatted_address || fallbackText || ""
  ).match(/\b(\d{5})(?:-\d{4})?\b/);
  return match?.[1] || "";
};

const geocodeZipFromLocation = (location) =>
  new Promise((resolve) => {
    if (!location || !window.google?.maps?.Geocoder) {
      resolve("");
      return;
    }
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location }, (results, status) => {
      if (status !== "OK" || !results?.length) {
        resolve("");
        return;
      }
      for (const result of results) {
        const zip = getPostalCode(result);
        if (zip) {
          resolve(zip);
          return;
        }
      }
      resolve("");
    });
  });

export default {
  name: "AddressInfoSection",
  props: {
    state: {
      type: Object,
    },
    disabled: {
      type: Boolean,
      default: false
    },
    searchObject: {
      type: Object,
    },
  },
  setup(props) {
    // Reactive form data
    const form = reactive({
      address: "",
      address1: "",
      address2: "",
      city: "",
      state: "",
      zip: "",
      county: "",
    });

    // Validation rules
    const rules = computed(() => ({
      form: {
        address: { required },
        address1: { required },
        city: { required },
        state: { required },
        zip: { required },
      },
    }));

    const v$ = useVuelidate(rules, { form });

    onMounted(() => {
      // vue-google-autocomplete may render its own internal input; bind it to our label for WAVE/ADA.
      const host = document.getElementById("address");
      const input =
        host?.tagName === "INPUT"
          ? host
          : host?.querySelector?.("input") || document.querySelector('input.pac-target-input');
      if (input) {
        input.id = "address";
        input.setAttribute("aria-labelledby", "address-label");
      }
    });
    // Populate the form with state.member data if available
    watchEffect(() => {
      if (props.state.member && form.address == '') {
        console.log('nehal')
        form.address1 = props.state.member.address || "";
        form.address = [
          props.state.member.address,
          props.state.member.city,
          props.state.member.state,
          props.state.member.zip
        ].filter(Boolean).join(" ");

        setTimeout(() => {
          const el = document.getElementById("address");
          if (el) el.value = form.address;
        }, 200);
        form.city = props.state.member.city || "";
        form.state = props.state.member.state || "";
        form.zip = props.state.member.zip || "";
      }
      if (props.state.reset_form == true) {
        Object.entries(form).forEach(([key, val]) => {
          console.log(val)
          form[key] = ""; // Correct for Vue 3
        });
        setTimeout(() => {
          const el = document.getElementById("address");
          if (el) el.value = "";
        }, 200);
        v$.value.$reset();
      }
    });

    return {
      form,
      v$,
      googleAutocompleteFields: GOOGLE_AUTOCOMPLETE_FIELDS,
    };
  },
  components: {
    VueGoogleAutocomplete,
  },
  mounted() {
    this.$nextTick(() => {
      const autocomplete = this.$refs.address?.autocomplete;
      autocomplete?.setFields?.(GOOGLE_AUTOCOMPLETE_FIELDS);
      // Legacy behavior: old :types="['address']" did not apply a strict filter.
      autocomplete?.setTypes?.([]);
    });
  },
  methods: {
    submitForm: async function () {
      this.v$.$touch();
      if (!this.v$.$invalid) {
        if (this.state.opt_type == "SEIU721" && (!this.state.company_number || this.state.company_number === "")) {
          Swal.fire({
            title: "Error!",
            text: "Please select company before proceeding!",
            icon: "error",
            confirmButtonText: "OK",
          });
          return false;
        }
        this.$emit("updateState", {
          key: "member",
          value: {
            address: this.form.address,
            address1: this.form.address1,
            address2: this.form.address2,
            city: this.form.city,
            state: this.form.state,
            zip: this.form.zip,
          },
        });
        this.$emit("updateState", { key: "cdl_plan", value: false });
        this.$emit("updateState", { key: "payment_method", value: "" });
        this.$emit("getEffDate");

        const state = filter(this.state.states, { abbreviation: this.form.state });
        if (state.length == 0) {
          this.$emit("updateState", { key: "disp_enroll_section", value: false });
          Swal.fire({
            title: "Error!",
            text: "State field is invalid.",
            icon: "error",
            confirmButtonText: "OK",
          });
          return false;
        }

        if (this.state.opt_type === "direct_page" || this.state.enroll_option === "affilliated") {
          if (this.state.opt_type === "direct_page") {
            this.$emit("updateState", { key: "company_number", value: this.searchObject.opt });
          }
          if (this.state.enroll_option === "affilliated") {
            this.$emit("updateState", { key: "company_number", value: this.state.company.number });
          }
        }

        let payload = {
          state: this.state.member.state,
          opt_type: this.state.opt_type,
          referer_url: this.state.referer_url,
          api_name: "getPlanSettings",
          is_list: true,
          city: this.state.member.city,
          zip: this.state.member.zip,
        };

        if (this.state.enroll_option != "individual") {
          payload = {
            company: this.state.company_number,
            opt_type: this.state.opt_type,
            referer_url: this.state.referer_url,
            api_name: "getPlanSettings",
            is_list: false,
            city: this.state.member.city,
            zip: this.state.member.zip,
          };
        }

        if (this.searchObject.opt === "mdsid") {
          this.$emit("updateState", {
            key: "company_number",
            value: this.state.company.company,
          });

          payload = {
            company: this.state.company.company,
            referer_url: this.state.referer_url,
            api_name: "getPlanSettings",
            city: this.state.member.city,
            zip: this.state.member.zip,
          };
        }

        console.log(payload);
        await this.$emit("getPlan", payload);
      }
    },
    resetForm: function () {
        Object.entries(this.form).forEach(([key, val]) => {
          console.log(val)
          this.form[key] = "";
          this.$emit("updateState", { key: "member." + key, value: "" });
        });
     
      setTimeout(() => {
        const el = document.getElementById("address");
        if (el) el.value = "";
        this.$emit("resetForm");
      }, 400);
      this.v$.$reset();
    },
    handlePlaceChange(place, rawPlace) {
      setTimeout(() => {
        let address2;
        const autocompleteText = this.$refs.address?.autocompleteText || "";
        if (autocompleteText) {
          address2 = autocompleteText.split(",");
        }
        if (place.street_number != undefined) {
          this.form.address1 = place.street_number + " " + place.route;
        } else {
          this.form.address1 = place.route;
        }
        if (place.administrative_area_level_2) {
          this.form.county = place.administrative_area_level_2;
        }
        if (address2?.length > 1) {
          if (
            place.locality?.toLowerCase().replace(/\s+/g, "") !=
            address2[1].toLowerCase().replace(/\s+/g, "")
          ) {
            // optional address2 line intentionally left blank
          } else {
            this.form.address2 = "";
          }
        }
        this.form.city = place.locality;
        this.form.state = place.administrative_area_level_1;
        this.form.zip =
          place.postal_code ||
          getPostalCode(rawPlace, autocompleteText);

        const applyGeocodedZip = () => {
          const location = rawPlace?.geometry?.location;
          if (!location || this.form.zip) return;
          geocodeZipFromLocation(location).then((zip) => {
            if (zip) this.form.zip = zip;
          });
        };

        if (!this.form.zip && rawPlace?.place_id && window.google?.maps?.places) {
          const service = new window.google.maps.places.PlacesService(
            document.createElement("div")
          );
          service.getDetails(
            { placeId: rawPlace.place_id, fields: GOOGLE_AUTOCOMPLETE_FIELDS },
            (details, status) => {
              if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                this.form.zip =
                  getPostalCode(details, autocompleteText) || this.form.zip;
              }
              applyGeocodedZip();
            }
          );
        } else {
          applyGeocodedZip();
        }
      }, 300);
    },
  },
};
</script>


<style scoped>
/* Darken placeholders for WCAG contrast (Address step only). */
.address-info :deep(input::placeholder),
.address-info :deep(textarea::placeholder) {
  color: #6c6c6c !important;
  opacity: 1;
}

/* Darken muted labels for WCAG contrast (Address step only). */
.address-info .address-label {
  color: #595959 !important;
}

/* WCAG: increase contrast for required asterisk only (Address step only). */
.address-info .required-asterisk {
  color: #b00020 !important;
}

/* WCAG: increase contrast for validation error text only (Address step only). */
.address-info :deep(small.text-error) {
  color: #b00020 !important;
}
</style>
